/**
 * MergeBranchesModal — Clinic Owner only.
 *
 * 2026-02-19 · Four-step wizard that reassigns every record carrying
 * `branch_id` from a chosen SOURCE to a chosen TARGET, then permanently
 * deletes the source. Irreversible by design — the final step forces
 * the owner to type the source branch's name verbatim.
 *
 * Steps:
 *   1. Select SOURCE branch (the duplicate / to-retire branch).
 *   2. Select TARGET branch (the keeper). Must differ from source.
 *   3. Preview — plain-English summary of what the merge will do.
 *   4. Confirm — type the source name + hit "Merge Permanently".
 *
 * The backend endpoint `POST /api/branches/{source_id}/merge` does the
 * actual work; this modal is pure UI.
 */
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, ArrowRight, ChevronLeft, Loader2 } from 'lucide-react';
import ModalShell from '../../components/ModalShell';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MergeBranchesModal({ branches, onClose, onMerged }) {
  const [step, setStep] = useState(1);
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // 2026-02-19 · Dry-run preview counts, fetched once the owner lands
  // on step 3 so the Preview card shows the real impact ("142 patients,
  // 318 appointments…") instead of generic prose.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const source = useMemo(() => branches.find((b) => b.branch_id === sourceId), [branches, sourceId]);
  const target = useMemo(() => branches.find((b) => b.branch_id === targetId), [branches, targetId]);

  // Lazy-fetch the dry-run exactly when the owner arrives on step 3.
  useEffect(() => {
    if (step !== 3 || !sourceId || !targetId) return;
    let cancelled = false;
    setPreviewLoading(true); setPreview(null);
    axios
      .get(`${API}/branches/${sourceId}/merge-preview`, { params: { target_id: targetId } })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || 'Preview failed'); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [step, sourceId, targetId]);

  const canStep1 = !!sourceId;
  const canStep2 = !!targetId && targetId !== sourceId;
  const confirmOk = confirmText.trim().toLowerCase() === (source?.name || '').trim().toLowerCase() && !!confirmText.trim();

  const submit = async () => {
    if (!confirmOk) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/branches/${sourceId}/merge`, {
        target_id: targetId,
        confirm_name: confirmText.trim(),
      });
      toast.success(`"${r.data.source_name}" merged into "${r.data.target_name}" successfully.`);
      onMerged?.(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Merge failed';
      setErr(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={busy ? undefined : onClose} cardClassName="max-w-xl w-full p-5" testid="merge-branches-modal">
      <h3 className="text-base font-bold mb-1 flex items-center gap-1.5 text-slate-800">
        Merge Branches
        <span className="text-[10px] font-semibold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded border border-rose-200">Irreversible</span>
      </h3>
      <div className="text-[11px] text-slate-500 mb-3">Step {step} of 4</div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-100 rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-indigo-500 transition-all" style={{ width: `${(step / 4) * 100}%` }} />
      </div>

      {err && (
        <div className="mb-3 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="merge-err">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />{err}
        </div>
      )}

      {step === 1 && (
        <div data-testid="merge-step-source">
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Select the branch to MERGE FROM
            <span className="block text-[10px] font-normal text-slate-500 mt-0.5">
              This is the duplicate or unwanted branch — it will be permanently deleted after the merge.
            </span>
          </label>
          <select
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            data-testid="merge-source-select"
            className="w-full border border-slate-300 rounded px-2 py-2 text-sm bg-white"
          >
            <option value="">— choose a branch —</option>
            {branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>
                {b.name}{b.is_primary ? ' · Main' : ''}{b.active === false ? ' (inactive)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {step === 2 && (
        <div data-testid="merge-step-target">
          <div className="text-xs text-slate-600 mb-2">
            Merging FROM: <b data-testid="merge-source-label">{source?.name}</b>
          </div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Select the branch to MERGE INTO
            <span className="block text-[10px] font-normal text-slate-500 mt-0.5">
              This is the branch to keep — all records will be moved here.
            </span>
          </label>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            data-testid="merge-target-select"
            className="w-full border border-slate-300 rounded px-2 py-2 text-sm bg-white"
          >
            <option value="">— choose a branch —</option>
            {branches
              .filter((b) => b.branch_id !== sourceId && b.active !== false)
              .map((b) => (
                <option key={b.branch_id} value={b.branch_id}>
                  {b.name}{b.is_primary ? ' · Main' : ''}
                </option>
              ))}
          </select>
          {targetId === sourceId && (
            <div className="text-[11px] text-rose-600 mt-1">Source and target must be different branches.</div>
          )}
        </div>
      )}

      {step === 3 && source && target && (
        <div data-testid="merge-step-preview" className="space-y-3">
          <div className="flex items-center justify-center gap-3 bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">From</div>
              <div className="text-sm font-bold text-slate-800">{source.name}</div>
            </div>
            <ArrowRight size={18} className="text-indigo-500" />
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">To</div>
              <div className="text-sm font-bold text-slate-800">{target.name}</div>
            </div>
          </div>

          {/* 2026-02-19 · Dry-run per-collection counts */}
          {previewLoading && (
            <div className="text-xs text-slate-500 italic flex items-center gap-1.5" data-testid="merge-preview-loading">
              <Loader2 size={12} className="animate-spin" /> Calculating impact…
            </div>
          )}
          {preview && (
            <div className="bg-indigo-50/50 border border-indigo-200 rounded p-3" data-testid="merge-preview-counts">
              <div className="text-[11px] font-semibold text-indigo-900 mb-1.5">
                You will move <b data-testid="merge-preview-total">{preview.total_records.toLocaleString('en-IN')}</b> record{preview.total_records === 1 ? '' : 's'}
                {preview.users_to_reassign > 0 && (
                  <> and reassign <b>{preview.users_to_reassign}</b> staff member{preview.users_to_reassign === 1 ? '' : 's'}</>
                )}.
              </div>
              {Object.keys(preview.counts).length === 0 ? (
                <div className="text-[11px] text-slate-500 italic">No records on the source branch — merge will only delete the empty row.</div>
              ) : (
                <ul className="text-[11px] text-slate-700 columns-2 gap-x-6">
                  {Object.entries(preview.counts).map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-2 break-inside-avoid">
                      <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                      <b className="font-mono">{v.toLocaleString('en-IN')}</b>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-5">
            <li>All patients, appointments, transactions, and records under <b>{source.name}</b> will be moved to <b>{target.name}</b>.</li>
            <li>Staff assigned only to <b>{source.name}</b> will be reassigned to <b>{target.name}</b>.</li>
            <li><b>{source.name}</b> will be permanently deleted after merge.</li>
            {source.is_primary && (
              <li className="text-amber-700">
                The <b>Main</b> designation will transfer to <b>{target.name}</b>.
              </li>
            )}
          </ul>
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-800">
            <AlertTriangle size={13} className="inline mr-1" />
            <b>This action cannot be undone.</b>
          </div>
        </div>
      )}

      {step === 4 && source && target && (
        <div data-testid="merge-step-confirm" className="space-y-3">
          <div className="text-xs text-slate-700">
            Type the source branch name below to confirm the merge:
            <div className="font-mono text-sm font-bold bg-slate-50 border border-slate-200 rounded px-2 py-1 mt-1 inline-block">
              {source.name}
            </div>
          </div>
          <input
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={source.name}
            data-testid="merge-confirm-input"
            className="w-full border border-rose-300 rounded px-2 py-2 text-sm focus:outline-none focus:border-rose-500"
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-5 pt-3 border-t border-slate-100">
        <button
          onClick={step === 1 ? onClose : () => setStep((s) => Math.max(1, s - 1))}
          disabled={busy}
          data-testid="merge-back-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded"
        >
          {step === 1 ? 'Cancel' : (<><ChevronLeft size={12} /> Back</>)}
        </button>

        {step < 4 ? (
          <button
            onClick={() => setStep((s) => s + 1)}
            disabled={(step === 1 && !canStep1) || (step === 2 && !canStep2)}
            data-testid="merge-next-btn"
            className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded shadow-sm"
          >
            Next
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={!confirmOk || busy}
            data-testid="merge-submit-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white rounded shadow-sm"
          >
            {busy ? (<><Loader2 size={12} className="animate-spin" /> Merging…</>) : 'Merge Permanently'}
          </button>
        )}
      </div>
    </ModalShell>
  );
}
